import { useEffect, useMemo } from 'react';
import { LIVE_MOVE_EPSILON, toFiniteNumber } from '../dashboardUtils';

export default function useOverviewPriceFreshness({
    userUid,
    activeView,
    combinedHoldings,
    liveQuotesByCogniveId,
    overviewSnapshotAt,
    liveQuotesLastUpdatedAt,
    autoRefreshEnabled,
    priceRefreshLoading,
    matricesLoading,
    fetchPriceRefreshStatus,
    refreshPriceData,
    notifyPendingPriceRefresh,
    setPendingAutoRefresh,
    importApplyInFlightRef,
    priceRefreshInFlightRef,
    coreLoadInFlightRef,
    lastImmediatePriceProbeRef,
    priceWatermarkBaseline,
}) {
    const overviewPriceFreshness = useMemo(() => {
        if (!combinedHoldings.length) {
            return {
                hasSnapshot: false,
                isEmpty: true,
                staleCount: 0,
                totalCount: combinedHoldings.length,
                maxAbsMovePct: 0,
                estImpact: null,
                detailText: 'No portfolio positions available yet.',
                tone: 'info',
            };
        }

        if (!overviewSnapshotAt) {
            return {
                hasSnapshot: false,
                isEmpty: false,
                staleCount: 0,
                totalCount: combinedHoldings.length,
                maxAbsMovePct: 0,
                estImpact: null,
                detailText: 'Overview snapshot pending.',
                tone: 'info',
            };
        }

        const snapshotTimestamp = overviewSnapshotAt.getTime();
        let staleCount = 0;
        let maxAbsMovePct = 0;
        let estImpact = 0;
        let hasImpact = false;

        combinedHoldings.forEach((holding) => {
            const cogniveId = holding?.cognive_id == null
                ? ''
                : String(holding.cognive_id).trim();
            if (!cogniveId) return;

            const quote = liveQuotesByCogniveId[cogniveId];
            if (!quote) return;

            const snapshotPrice = toFiniteNumber(holding?.price);
            const latestPrice = toFiniteNumber(quote?.price);
            if (snapshotPrice === null || latestPrice === null || snapshotPrice === 0) {
                return;
            }

            const quoteUpdatedAtMs = Number(quote?.updated_at_ms);
            const quoteIsNewer = Number.isFinite(quoteUpdatedAtMs)
                ? quoteUpdatedAtMs > snapshotTimestamp
                : latestPrice !== snapshotPrice;

            const deltaAbs = latestPrice - snapshotPrice;
            const deltaPct = deltaAbs / snapshotPrice;
            const hasMove = quoteIsNewer && Math.abs(deltaAbs) > LIVE_MOVE_EPSILON;
            if (!hasMove) {
                return;
            }

            staleCount += 1;
            maxAbsMovePct = Math.max(maxAbsMovePct, Math.abs(deltaPct));

            const quantity = toFiniteNumber(holding?.quantity);
            if (quantity !== null) {
                estImpact += quantity * deltaAbs;
                hasImpact = true;
            }
        });

        if (staleCount === 0) {
            return {
                hasSnapshot: true,
                isEmpty: false,
                staleCount: 0,
                totalCount: combinedHoldings.length,
                maxAbsMovePct: 0,
                estImpact: null,
                detailText: 'Aligned with latest stored prices.',
                tone: 'success',
            };
        }

        return {
            hasSnapshot: true,
            isEmpty: false,
            staleCount,
            totalCount: combinedHoldings.length,
            maxAbsMovePct,
            estImpact: hasImpact ? estImpact : null,
            detailText: `${staleCount} holding${staleCount === 1 ? '' : 's'} have newer prices than the current app snapshot.`,
            tone: 'warning',
        };
    }, [overviewSnapshotAt, combinedHoldings, liveQuotesByCogniveId]);

    useEffect(() => {
        if (!userUid) return;
        if (activeView !== 'overview') return;
        if (!overviewPriceFreshness.hasSnapshot) return;
        if (overviewPriceFreshness.staleCount <= 0) return;
        if (!liveQuotesLastUpdatedAt) return;
        // The deferred core bootstrap owns the initial price-watermark
        // baseline. Until it publishes that watermark, an immediate quote
        // probe would query from the beginning of time and mistake all stored
        // prices for a post-bootstrap change.
        if (!priceWatermarkBaseline) return;
        if (importApplyInFlightRef.current) return;
        if (priceRefreshLoading || priceRefreshInFlightRef.current || coreLoadInFlightRef.current || matricesLoading) {
            return;
        }

        const probeKey = liveQuotesLastUpdatedAt instanceof Date
            ? liveQuotesLastUpdatedAt.getTime()
            : new Date(liveQuotesLastUpdatedAt).getTime();
        if (!Number.isFinite(probeKey)) return;
        if (lastImmediatePriceProbeRef.current === probeKey) return;

        lastImmediatePriceProbeRef.current = probeKey;

        void (async () => {
            const status = await fetchPriceRefreshStatus({ silent: true });
            if (!status) return;

            if (status.has_newer_prices) {
                setPendingAutoRefresh(status);
                if (autoRefreshEnabled) {
                    await refreshPriceData({ reason: 'auto_price_refresh' });
                } else {
                    notifyPendingPriceRefresh(status);
                }
            } else {
                setPendingAutoRefresh(null);
            }
        })();
    }, [
        userUid,
        activeView,
        autoRefreshEnabled,
        overviewPriceFreshness.hasSnapshot,
        overviewPriceFreshness.staleCount,
        liveQuotesLastUpdatedAt,
        priceWatermarkBaseline,
        priceRefreshLoading,
        matricesLoading,
        fetchPriceRefreshStatus,
        refreshPriceData,
        notifyPendingPriceRefresh,
        setPendingAutoRefresh,
        importApplyInFlightRef,
        priceRefreshInFlightRef,
        coreLoadInFlightRef,
        lastImmediatePriceProbeRef,
    ]);

    return {
        overviewPriceFreshness,
    };
}
